import { AppChatRules } from "./AppChatRules";
import { AppFrameWide } from "./AppFrameWide";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAiChat, AppAiChatDoc, AppAiChatItem } from "./AppAiChat";
import { AppAssistCitations } from "./AppAssistCitations";
import { AppAssistAnswer, AppAssistApi, AppAssistTurn } from "./AppAssistApi";
import { AppAssistRich } from "./AppAssistRich";
import { AppAssistStream, AppAssistStreamRich } from "./AppAssistStream";
import { AppOutbox } from "./AppOutbox";

// AppAssistRules - the "ask AI about this chat" panel's memory for one contact, and the small
// decisions around the chat card (owner, 2026-09-25: "Chatting with AI about this chat must be a
// PROMINENT feature").
//
// ⚠ RULES ONLY - no DOM, no `window`, no transport. `AppAssistPanel` is the act; `AppAssistApi` is
// the wire. Kept apart so every decision here is asserted without a browser.
//
// ⛔ DECISION 9: NOTHING HERE SENDS. A draft is text; the only way it leaves is the owner pressing
// a Send button - the composer's, or the draft card's - and both go through `AppComposer.SendText`
// and `/send`, exactly like a message they typed. `SendState` only RECORDS what that press did.
//
// ⚠ PRIVATE DATA: questions, answers and drafts are the owner's conversations. Nothing in this file
// or its callers may write them to the console.
//
// ⭐ THE HISTORY IS KEPT PER CONTACT (owner, 2026-09-26: "AI chat history should be persisted and always
// visible - per contact person"). The service appends every answered question to the contact's
// `ai-chat.json` in the owner's Dropbox; the panel reads it when the contact opens (`Adopt`) and draws it
// above this visit's entries, with a line for each day.
// ⛔ ONLY THE NEWEST DRAFT CAN BE SENT, AND ONLY WHILE NOTHING HAS BEEN DONE WITH IT (`NewestDraftAt`,
// `CanSendAt`). An older draft - or one already sent, taken into the box or skipped - shows its text,
// Copy, and what became of it. Decision 9 is a human click on exactly the text shown; a one-click send of
// a stale draft from last week, or a second send of one that already went, is not that.

/** One question and what came back. `Ok` false means `Why` is the sentence to show. */
export class AppAssistEntry {
    public SourceKeys: string[] = [];
    public SourceTrust: string = "";
    public Ask: string = "";
    public Answer: string = "";
    public Draft: string = "";
    public Ok: boolean = false;
    public Why: string = "";
    /** The draft card's own send: "" (not sent), `Sending` or `Sent` (the request was accepted). */
    public SendState: string = "";
    /** Why the card's last send did not go - shown under the card, and Send can be pressed again. */
    public SendWhy: string = "";
    /** The history item that carries the draft - where the outcome is recorded - or "" (not saved). */
    public Id: string = "";
    /** Every history item this entry stands for; empty = not in the file (asked on this visit, not saved). */
    public Ids: string[] = [];
    /** What the owner did with the draft: `sent`, `edited`, `skipped`, or "". */
    public Outcome: string = "";
    /** When (ISO-8601 UTC), for the day lines. "" = now. */
    public At: string = "";
    /** Read from `ai-chat.json`, not asked on this visit. */
    public FromFile: boolean = false;
    /** A closed task's entry (the task-close card's record): its task id, else "". */
    public TaskId: string = "";
    /** The ready-made question's id it was asked with (`AppAssistApi.PresetIds`), "" for a typed one - Try again re-sends it. */
    public Preset: string = "";
    /**
     * ⭐ A question that got NO answer (timed out, failed) keeps the words that had already streamed (2026-09-29):
     * shown as a partial answer above the sentence and Try again. Text only - a table/chart slot is dropped.
     */
    public Partial: string = "";
    /**
     * ⭐ "Remove from history" (2026-10-01, `AppAssistForget`): "" (nothing asked), `ask` (the inline "Remove this
     * answer?" is showing) or `going` (the request is in flight - the entry is faded until the service answers).
     */
    public Forget: string = "";
    /** Why the last removal did not happen - the service's sentence, in ink under the entry. */
    public ForgetWhy: string = "";
    /** ⛔ This draft can no longer be sent or edited on this visit - see `AppAssistRules.Retire`. */
    public Retired: boolean = false;
}

/** A run of answer text and how it is marked. ⚠ Always drawn as TEXT (`textContent`). */
export class AppAssistSpan {
    public Text: string = "";
    public Bold: boolean = false;
    public Italic: boolean = false;
    public Code: boolean = false;
}

/**
 * One block of an answer: a paragraph (`Lines` are its lines), a list (`Lines` are its items), a
 * Markdown pipe `table` (`Head`, `Rows`, `Align`) or a `rich` ```wac-html fence (`Source`, raw -
 * ⛔ never markup until `kHtmlSafeCore.Clean` has cut it down, see `AppAssistRich`).
 */
export class AppAssistBlock {
    /** `p`, `ul`, `ol`, `table` or `rich`. */
    public Kind: string = "p";
    /** The first number of an `ol`. */
    public Start: number = 1;
    public Lines: AppAssistSpan[][] = [];
    /** A `table`'s header cells, each its runs. */
    public Head: AppAssistSpan[][] = [];
    /** A `table`'s body rows - exactly as many cells as `Head`, each its runs. */
    public Rows: AppAssistSpan[][][] = [];
    /** A `table`'s column alignment: "left", "center", "right" or "" per column. */
    public Align: string[] = [];
    /** A `rich` block's source between its fence lines, untouched. */
    public Source: string = "";
}

/** What one line of an answer is: `p` text, a `ul`/`ol` item (`Rest` without its marker), or `blank`. */
export class AppAssistLine {
    public Kind: string = "p";
    public Start: number = 1;
    public Rest: string = "";
}

/**
 * The panel's conversation for ONE contact, kept in memory for the visit.
 *
 * ⚠ `Seq` IS WHAT MAKES CANCEL WORK. An answer can take a minute and cannot be recalled from the
 * service, so "cancel" means "ignore the late answer": every ask and every cancel moves `Seq` on,
 * and an answer is taken only for the `Seq` it was asked with.
 */
export class AppAssistSession {
    public Entries: AppAssistEntry[] = [];
    public Busy: boolean = false;
    public Asking: string = "";
    public Seq: number = 0;
    /** The saved history: "" (not read), `loading`, `loaded`, `unreadable` or `failed`. */
    public HistoryState: string = "";
    /**
     * ⭐ THE ANSWER SO FAR while it is being written (`AppAssistStream.Visible` - never the draft, never
     * table/chart markup). "" when nothing has arrived; gone when the final answer is taken.
     */
    public Streaming: string = "";
    /**
     * ⭐ What the stream said of each table/chart slot of `Streaming` (`AppAssistStream.Parts`) - set with it
     * by `AppAssistCall.OnText`, read only while `Streaming` is not "".
     */
    public StreamRich: AppAssistStreamRich[] = [];
    /** The preset id of the question being asked ("" = typed). */
    public Preset: string = "";
    /** ⭐ The question is waiting for the service to finish restoring the archive (a `retry` refusal, 2026-09-29). */
    public Waiting: boolean = false;
    /** History item ids removed on this visit ("Remove from history") - a late read of the old file never brings them back. */
    public Forgotten: Record<string, boolean> = {};
    /** Draft item ids retired on this visit (`AppAssistRules.Retire`) - they never offer Send or Edit again. */
    public Retired: Record<string, boolean> = {};
    /** When (ms) a turn was last removed on this visit - a click on a Send that the removal moved is not a send. */
    public ForgotAtMs: number = 0;
    /** The saved ids the other tabs were last told about, or that a read of the file brought (`AppAssistRules.NewlySaved`). */
    public ToldSig: string = "";
}

export class AppAssistRules {
    public static readonly EmbeddedAttr: string = "data-wac-embedded";
    public static readonly Sending: string = "sending";
    public static readonly Sent: string = "sent";
    /** A first name longer than this makes "Send to …" too wide for the card; the button says "Send". */
    public static readonly SendNameMax: number = 14;
    /** The box's ceiling: five lines of the composer's 15px/1.5 type plus its padding. */
    public static readonly ComposerMaxPx: number = 134;

    /**
     * Start asking `Ask`. Answers "" when the ask may go (the session is now busy on a new `Seq`),
     * else the sentence to show - validation, law 1, never the error handler.
     */
    public static Begin = (Session: AppAssistSession, Ask: string, Preset: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistApi.LostWhy(); }
        try {
            const Text: string = nz(Ask).trim();
            if (Session.Busy === true) { return "Still thinking about the last question - cancel it to ask another."; }
            const Empty: boolean = Text.length === 0;
            if (Empty) { return "Type a question first."; }
            const Long: boolean = Text.length > AppAssistApi.MaxAsk;
            if (Long) {
                return `That question is too long - keep it under ${String(AppAssistApi.MaxAsk)} characters.`;
            }
            Session.Seq = Session.Seq + 1;
            Session.Busy = true;
            Session.Asking = Text;
            Session.Streaming = "";
            Session.Waiting = false;
            Session.Preset = "";
            if (AppAssistApi.PresetIds.indexOf(nz(Preset)) >= 0) { Session.Preset = Preset; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Begin");
            return AppAssistApi.LostWhy();
        }
    };

    /**
     * An answer arrived for the ask made with `Seq`. False - and nothing recorded - when that ask was
     * cancelled or overtaken, so a late answer never lands under a question the owner walked away from.
     */
    public static Take = (Session: AppAssistSession, Seq: number, Answer: AppAssistAnswer, NowIso: string = ""): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Session.Busy !== true || Seq !== Session.Seq) { return false; }
            const Entry: AppAssistEntry = new AppAssistEntry();
            Entry.Ask = Session.Asking;
            Entry.Ok = Answer.Ok === true;
            Entry.At = nz(NowIso);
            // ⛔ A REFUSAL NEVER CARRIES A DRAFT OR AN ANSWER - only its sentence.
            if (Entry.Ok === true) {
                Entry.Answer = nz(Answer.Answer);
                Entry.SourceKeys = Answer.SourceKeys.slice();
                Entry.SourceTrust = Answer.SourceTrust;
                Entry.Draft = nz(Answer.Draft).trim();
                // ⭐ SAVED IN THE HISTORY: its ids (so a reload does not show it twice) and the draft's item.
                if (Answer.Saved === true) { Entry.Ids = Answer.Ids.slice(); Entry.Id = nz(Answer.DraftId); }
            }
            if (Entry.Ok !== true) { Entry.Why = nz(Answer.Why); Entry.Partial = AppAssistRules.PartialOf(Session.Streaming); }
            Entry.Preset = Session.Preset;
            Session.Entries.push(Entry);
            Session.Busy = false;
            Session.Asking = "";
            Session.Streaming = "";
            Session.Waiting = false;
            Session.Preset = "";
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Take");
            return false;
        }
    };

    /**
     * What a question that got no answer keeps of its stream: the words (already `AppAssistStream.Visible` - never a
     * draft, never markup) without the table/chart placeholder lines, trimmed. "" when nothing had streamed.
     */
    public static PartialOf = (Streamed: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Kept: string[] = [];
            for (const Line of nz(Streamed).split("\n")) {
                if (Line.trim() === AppAssistStream.Placeholder) { continue; }
                Kept.push(Line);
            }
            return Kept.join("\n").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.PartialOf");
            return "";
        }
    };

    /** Stop waiting. The answer, if it ever comes, is ignored (`Take`). */
    public static Cancel = (Session: AppAssistSession): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Session.Busy !== true) { return; }
            Session.Seq = Session.Seq + 1;
            Session.Busy = false;
            Session.Asking = "";
            Session.Streaming = "";
            Session.Waiting = false;
            Session.Preset = "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Cancel");
        }
    };

    /**
     * The earlier turns a follow-up carries. ⚠ ONLY ANSWERED ONES: a refusal is not something the
     * model said, and sending "AI help isn't available yet" back as its own answer would teach it
     * nothing but noise. `AppAssistApi.Body` keeps the newest `MaxTurns` and cuts each one.
     */
    public static TurnsOf = (Session: AppAssistSession): AppAssistTurn[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistTurn[] = [];
            for (const One of Session.Entries) {
                if (One.Ok !== true) { continue; }
                // ⭐ A SAVED TURN IS READ BY THE SERVICE FROM `ai-chat.json` - sending it too would say it twice.
                if (One.FromFile === true || One.Ids.length > 0) { continue; }
                const Turn: AppAssistTurn = new AppAssistTurn();
                Turn.Ask = One.Ask;
                // ⚠ THE DRAFT IS PART OF WHAT WAS SAID: "make it shorter" must know which text.
                // ⚠ A TABLE OR CHART GOES BACK AS "[table]"/"[chart]" (`AppAssistRich.Summary`) - its
                // markup would spend the turn's whole `MaxTurnAnswer` and be cut mid-tag.
                Turn.Answer = AppAssistRich.Summary(One.Answer);
                if (One.Draft.length > 0) { Turn.Answer = (Turn.Answer + "\n\nDraft: " + One.Draft).trim(); }
                Out.push(Turn);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.TurnsOf");
            return [];
        }
    };

    /**
     * Whether a contact "name" is really an address - `lid:…`, anything with `@`, or a long run of
     * bare digits (a privacy id, never a phone number, which the index writes with its `+`).
     * ⚠ A raw id in the composer ("Message lid:1234…") reads as a broken product.
     */
    public static IsRawId = (Name: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const Text: string = nz(Name).trim();
            if (Text.length === 0) { return true; }
            if (Text.toLowerCase().indexOf("lid:") === 0) { return true; }
            if (Text.indexOf("@") >= 0) { return true; }
            return /^\d{8,}$/.test(Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.IsRawId");
            return true;
        }
    };

    /** The composer's placeholder: "Message Anna", or plain "Message" for a name that is an id. */
    public static Placeholder = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Message"; }
        try {
            if (AppAssistRules.IsRawId(Title) === true) { return "Message"; }
            return "Message " + nz(Title).trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Placeholder");
            return "Message";
        }
    };

    /** The panel's heading: "Ask about Anna", or "Ask about this chat". */
    public static Heading = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Ask about this chat"; }
        try {
            if (AppAssistRules.IsRawId(Title) === true) { return "Ask about this chat"; }
            return "Ask about " + nz(Title).trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Heading");
            return "Ask about this chat";
        }
    };

    /**
     * Whether a key press in a message box submits it: Enter alone, on a device with a real keyboard.
     *
     * ⚠ NEVER ON A TOUCH SCREEN - a phone's Enter key is the only way to start a new line, and
     * WhatsApp itself treats it so. ⚠ NEVER MID-COMPOSITION (an IME confirming a word presses Enter).
     * ⛔ It is still a human press on the owner's own keyboard, so decision 9 holds: the submit runs
     * the same handler the Send button does, with every send rule.
     */
    public static EnterSends = (Key: string, Shift: boolean, Composing: boolean, Coarse: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Key !== "Enter") { return false; }
            return Shift !== true && Composing !== true && Coarse !== true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.EnterSends");
            return false;
        }
    };

    /** The height the composer box should take for content `ScrollPx` tall: grows to five lines, then scrolls. */
    public static FitPx = (ScrollPx: number, BorderPx: number): number => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistRules.ComposerMaxPx; }
        try {
            const Want: number = Math.ceil(ScrollPx + BorderPx);
            if (Want > AppAssistRules.ComposerMaxPx) { return AppAssistRules.ComposerMaxPx; }
            return Want;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.FitPx");
            return AppAssistRules.ComposerMaxPx;
        }
    };

    // ── The draft card's Send ────────────────────────────────────────────────────────────────

    /**
     * The card's Send button: "Send to Anna" for a name that fits, else plain "Send".
     * ⚠ NEVER A RAW ID OR A NUMBER ("Send to +46", "Send to lid:…") - those read as a broken product.
     */
    public static SendLabel = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "Send"; }
        try {
            if (AppAssistRules.IsRawId(Title) === true) { return "Send"; }
            const First: string = nz(Title).trim().split(/\s+/)[0].replace(/[.,;:!?]+$/, "");
            if (First.length === 0 || First.length > AppAssistRules.SendNameMax) { return "Send"; }
            if (/^[+\d(]/.test(First) === true) { return "Send"; }
            return "Send to " + First;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.SendLabel");
            return "Send";
        }
    };

    /**
     * Whether the card's Send may be pressed now: a draft that is there, within `/send`'s limit, and
     * not already sending or sent. ⛔ This is what makes a double click one message.
     */
    public static CanSend = (Entry: AppAssistEntry): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Entry.Ok !== true || Entry.SendState.length > 0) { return false; }
            // ⛔ SOMETHING WAS ALREADY DONE WITH IT (sent, taken into the box, skipped) - never offered again.
            if (Entry.Outcome.length > 0) { return false; }
            const Text: string = nz(Entry.Draft);
            return Text.trim().length > 0 && Text.length <= AppOutbox.MaxText;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.CanSend");
            return false;
        }
    };

    /**
     * The index of the NEWEST entry that carries a draft, or -1. ⛔ Only this one can ever be sent or
     * edited - see the header.
     */
    public static NewestDraftAt = (Session: AppAssistSession): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            for (let At: number = Session.Entries.length - 1; At >= 0; At = At - 1) {
                const One: AppAssistEntry = Session.Entries[At];
                if (One.Ok === true && One.Draft.length > 0) { return At; }
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.NewestDraftAt");
            return -1;
        }
    };

    /** ⛔ May the draft card at `At` be sent now: the newest draft, and `CanSend`. */
    public static CanSendAt(Session: AppAssistSession, At: number): boolean {
        const Broken: boolean = getCoreUtils().KErrorMode;
        if (Broken === true) { return false; }
        try {
            const InRange: boolean =
                At >= 0 && At < Session.Entries.length && At === AppAssistRules.NewestDraftAt(Session);
            if (InRange !== true) { return false; }
            const Card = Session.Entries[At];
            const Retired: boolean = AppAssistRules.IsRetired(Session, Card);
            if (Retired === true) { return false; }
            return AppAssistRules.CanSend(Card);
        } catch (e) {
            const Where: string = "AppAssistRules.CanSendAt";
            getCoreUtils().KGlobalErrorHandler(e, Where);
            return false;
        }
    }

    /** Whether the card at `At` still offers Send and Edit at all (the newest draft, nothing done with it). */
    public static LiveAt = (Session: AppAssistSession, At: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (At < 0 || At >= Session.Entries.length || At !== AppAssistRules.NewestDraftAt(Session)) { return false; }
            if (AppAssistRules.IsRetired(Session, Session.Entries[At]) === true) { return false; }
            return Session.Entries[At].Outcome.length === 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.LiveAt");
            return false;
        }
    };

    // ── "Remove from history" (`AppAssistForget`) ────────────────────────────────────────────

    /** DOCUMENTED EXCEPTION: pure lookup of values in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IsRetired = (Session: AppAssistSession, Entry: AppAssistEntry): boolean => {
        if (Entry.Retired === true) { return true; }
        return Entry.Id.length > 0 && Session.Retired[Entry.Id] === true;
    };

    /**
     * ⛔ A REMOVAL NEVER MAKES AN OLDER DRAFT SENDABLE. "Only the newest draft can be sent" would hand Send and Edit
     * to last week's draft the moment the newest turn is removed - on the very spot the owner was just clicking.
     * So before any entry leaves the log, every draft that is NOT live now is retired for the visit: it keeps Copy
     * and what became of it, and never offers Send or Edit again. The live draft (if it stays) is untouched.
     */
    public static Retire = (Session: AppAssistSession): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (let At: number = 0; At < Session.Entries.length; At = At + 1) {
                const One: AppAssistEntry = Session.Entries[At];
                if (One.Draft.length === 0 || AppAssistRules.LiveAt(Session, At) === true) { continue; }
                One.Retired = true;
                if (One.Id.length > 0) { Session.Retired[One.Id] = true; }
                // ⭐ SHOWN AS THE FILE WILL SAY IT: the service records the same drafts as `AppAiChat.RetiredOutcome` in
                // the removal's own write (`AppAiChat.Forget`), so this visit and every later one read alike.
                if (One.Outcome.length === 0 && One.SendState.length === 0) { One.Outcome = AppAiChat.RetiredOutcome; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Retire");
        }
    };

    /**
     * Whether an entry can be removed from the saved history: it is IN the file (it has item ids), and no send of
     * its draft is in flight. A question asked on this visit that was not saved has nothing to remove.
     * DOCUMENTED EXCEPTION: pure check of values in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static CanForget = (Entry: AppAssistEntry): boolean => {
        return Entry.Ids.length > 0 && Entry.SendState !== AppAssistRules.Sending;
    };

    /** The entry that stands for any of `Ids` now (a reload replaces the entry objects), or null. */
    public static EntryWith = (Session: AppAssistSession, Ids: string[]): AppAssistEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const One of Session.Entries) {
                for (const Id of One.Ids) { if (Ids.indexOf(Id) >= 0) { return One; } }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.EntryWith");
            return null;
        }
    };

    /**
     * The service removed the turn holding `Ids`: every entry standing for any of them leaves the log, the ids are
     * remembered (`Forgotten` - a read of the old file still in flight must not bring them back), and ⛔ every draft
     * that was not live is retired first (`Retire`). Answers how many entries left.
     */
    public static Forgot = (Session: AppAssistSession, Ids: string[], NowMs: number): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            AppAssistRules.Retire(Session);
            const Kept: AppAssistEntry[] = [];
            for (const One of Session.Entries) {
                let Gone: boolean = false;
                for (const Id of One.Ids) { if (Ids.indexOf(Id) >= 0) { Gone = true; } }
                if (Gone === false) { Kept.push(One); continue; }
                for (const Id of One.Ids) { Session.Forgotten[Id] = true; }
            }
            for (const Id of Ids) { Session.Forgotten[Id] = true; }
            const Left: number = Session.Entries.length - Kept.length;
            Session.Entries = Kept;
            Session.ForgotAtMs = NowMs;
            // ⚠ The removal has its own notice to the other tabs (`AppMain.AssistForgot`) - not a second one later.
            Session.ToldSig = AppAssistRules.SavedSig(Session);
            return Left;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Forgot");
            return 0;
        }
    };

    // ── The saved history (`ai-chat.json`) ───────────────────────────────────────────────────

    /**
     * The file's items as panel entries: each `ask` starts one; the `answer`, `draft` or `notice` after it
     * completes it. ⚠ Items whose question the cap cut away still show, under an empty question.
     */
    public static FromHistory = (Doc: AppAiChatDoc): AppAssistEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistEntry[] = [];
            if (Doc.Unreadable === true) { return Out; }
            let Open: AppAssistEntry | null = null;
            for (const One of Doc.Items) {
                if (One.Kind === "ask" || Open === null) {
                    Open = AppAssistRules.Started(One);
                    Out.push(Open);
                    if (One.Kind === "ask") { continue; }
                }
                AppAssistRules.Complete(Open as AppAssistEntry, One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.FromHistory");
            return [];
        }
    };

    /** A new entry from the item that opens it. */
    private static Started = (One: AppAiChatItem): AppAssistEntry => {
        if (getCoreUtils().KErrorMode === true) { return new AppAssistEntry(); }
        try {
            const Out: AppAssistEntry = new AppAssistEntry();
            Out.Ok = true;
            Out.FromFile = true;
            Out.At = One.At;
            Out.TaskId = One.TaskId;
            if (One.Kind === "ask") { Out.Ask = One.Text; Out.Ids.push(One.Id); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Started");
            return new AppAssistEntry();
        }
    };

    /** An answer, draft or notice onto its entry. */
    private static Complete = (Entry: AppAssistEntry, One: AppAiChatItem): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Entry.Ids.push(One.Id);
            if (One.TaskId.length > 0) { Entry.TaskId = One.TaskId; }
            if (One.Kind === "answer" || One.Kind === "notice") {
                if (Entry.Answer.length === 0) {
                    Entry.SourceKeys = One.SourceKeys.slice(); Entry.SourceTrust = One.SourceTrust;
                } else if (Entry.SourceTrust === AppAssistCitations.Trust && One.SourceTrust === AppAssistCitations.Trust) {
                    Entry.SourceKeys = Entry.SourceKeys.concat(One.SourceKeys);
                } else {
                    Entry.SourceKeys = []; Entry.SourceTrust = "";
                }
                Entry.Answer = (Entry.Answer + "\n\n" + One.Text).trim();
            }
            if (AppAiChat.SendableOf(One).length > 0) {
                Entry.Draft = AppAiChat.SendableOf(One).trim();
                Entry.Id = One.Id;
                Entry.Outcome = One.Outcome;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Complete");
        }
    };

    /**
     * The file was read: its entries replace the ones read before, and this visit's entries the file now
     * holds are dropped (matched by item id) - each question shows once. ⚠ A send in flight, or an outcome
     * noted on this visit but not yet in the file, carries over to the new entry for the same draft.
     */
    public static Adopt = (Session: AppAssistSession, Doc: AppAiChatDoc, Known: Record<string, boolean> | null = null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Doc.Unreadable === true) { Session.HistoryState = "unreadable"; return; }
            const Fresh: AppAssistEntry[] = AppAssistRules.Unforgotten(Session, AppAssistRules.FromHistory(Doc));
            const Held: Record<string, boolean> = {};
            for (const One of Fresh) { for (const Id of One.Ids) { Held[Id] = true; } }
            // ⛔ AN ENTRY THE FILE NO LONGER HOLDS (removed in another tab) MUST NOT HAND SEND TO AN OLDER DRAFT - `Retire`.
            if (AppAssistRules.AnyVanished(Session, Held, Known) === true) { AppAssistRules.Retire(Session); }
            const Live: AppAssistEntry[] = [];
            for (const Old of Session.Entries) {
                AppAssistRules.CarryTo(Fresh, Old);
                AppAssistRules.CarryForget(Fresh, Old);
                if (Old.FromFile === true || AppAssistRules.AnyHeld(Old, Held) === true) { continue; }
                // ⭐ ASKED ON THIS VISIT, SAVED, AND NOW GONE FROM THE FILE = removed in another tab: it leaves this log too.
                if (AppAssistRules.SavedBefore(Old, Known) === true) { continue; }
                Live.push(Old);
            }
            Session.Entries = Fresh.concat(Live);
            Session.HistoryState = "loaded";
            // ⚠ A read is not a save: what the file brought is nothing to tell the other tabs about (`NewlySaved`).
            Session.ToldSig = AppAssistRules.SavedSig(Session);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Adopt");
        }
    };

    /** A send in flight and an outcome noted locally carry over to the fresh entry for the same draft. */
    private static CarryTo = (Fresh: AppAssistEntry[], Old: AppAssistEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Old.Id.length === 0) { return; }
            for (const One of Fresh) {
                if (One.Id !== Old.Id) { continue; }
                One.SendState = Old.SendState;
                One.SendWhy = Old.SendWhy;
                if (One.Outcome.length === 0) { One.Outcome = Old.Outcome; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.CarryTo");
        }
    };

    /** The file's entries without the ones removed on this visit - a read of the old file may still be in flight. */
    private static Unforgotten = (Session: AppAssistSession, Fresh: AppAssistEntry[]): AppAssistEntry[] => {
        if (getCoreUtils().KErrorMode === true) { return Fresh; }
        try {
            const Out: AppAssistEntry[] = [];
            for (const One of Fresh) { if (AppAssistRules.AnyHeld(One, Session.Forgotten) === false) { Out.push(One); } }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Unforgotten");
            return Fresh;
        }
    };

    /** Whether an entry read from the file before is in the file no longer (`Held` = the ids it holds now). */
    private static AnyVanished = (Session: AppAssistSession, Held: Record<string, boolean>, Known: Record<string, boolean> | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            for (const Old of Session.Entries) {
                if (Old.Ids.length === 0 || AppAssistRules.AnyHeld(Old, Held) === true) { continue; }
                if (Old.FromFile === true || AppAssistRules.SavedBefore(Old, Known) === true) { return true; }
            }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.AnyVanished");
            return false;
        }
    };

    /** Every saved item id the log holds, in order, as one string. DOCUMENTED EXCEPTION: pure join. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static SavedSig = (Session: AppAssistSession): string => {
        const Out: string[] = [];
        for (const One of Session.Entries) { Out.push(...One.Ids); }
        const Joined: string = Out.join(",");
        return Joined;
    };

    /**
     * ⛔ THE OTHER TABS ARE TOLD ONLY WHEN THIS TAB SAVED SOMETHING (verifier, 2026-10-01): true ONCE each time the
     * log's saved ids differ from what was last told - an answer the service saved. A READ of the file (`Adopt`) and
     * a removal (`Forgot`, which has its own notice) move the mark without telling. Before this the page told the
     * other tabs after EVERY repaint of a log whose last entry was saved - a history read included - so each read
     * made every other tab read too, and that echo (inside the bus's "same notice within 2 s is one" window)
     * swallowed the real notice that followed: a question saved or removed elsewhere did not show up.
     */
    public static NewlySaved = (Session: AppAssistSession): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Sig: string = AppAssistRules.SavedSig(Session);
            if (Sig === Session.ToldSig) { return false; }
            Session.ToldSig = Sig;
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.NewlySaved");
            return false;
        }
    };

    /**
     * The saved item ids the session's entries hold NOW - taken when a read of the file STARTS (`AppAssistHistoryRead`)
     * and handed to `Adopt` as `Known`. DOCUMENTED EXCEPTION: pure collection. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static SavedIds = (Session: AppAssistSession): Record<string, boolean> => {
        const Out: Record<string, boolean> = {};
        for (const One of Session.Entries) { for (const Id of One.Ids) { Out[Id] = true; } }
        return Out;
    };

    /**
     * Whether `Entry` was ALREADY saved when the read began (`Known` = `SavedIds` at that moment). ⛔ THIS IS WHAT TELLS
     * "REMOVED ELSEWHERE" FROM "A READ THAT RACED THE SAVE": the service saves an answer before it answers the page,
     * so ids the page held before it asked for the file are in any file that read returns - unless the turn was
     * removed. An entry that got its ids AFTER the read began (or has none - not saved, or still being answered) is
     * kept. No `Known` (a caller that took no snapshot) = nothing is dropped.
     * DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    private static SavedBefore = (Entry: AppAssistEntry, Known: Record<string, boolean> | null): boolean => {
        if (Known === null || Entry.Ids.length === 0) { return false; }
        return AppAssistRules.AnyHeld(Entry, Known);
    };

    /** A removal being confirmed or in flight (and why the last one failed) carries over to the fresh entry for the same items. */
    private static CarryForget = (Fresh: AppAssistEntry[], Old: AppAssistEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Old.Forget.length === 0 && Old.ForgetWhy.length === 0) { return; }
            const Held: Record<string, boolean> = {};
            for (const Id of Old.Ids) { Held[Id] = true; }
            for (const One of Fresh) {
                if (AppAssistRules.AnyHeld(One, Held) === false) { continue; }
                One.Forget = Old.Forget;
                One.ForgetWhy = Old.ForgetWhy;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.CarryForget");
        }
    };

    /** DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static AnyHeld =(Entry: AppAssistEntry, Held: Record<string, boolean>): boolean => {
        for (const Id of Entry.Ids) { if (Held[Id] === true) { return true; } }
        return false;
    };

    /** How many entries came from the saved history - the ✦ button's count on a phone. */
    public static SavedCount = (Session: AppAssistSession): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Out: number = 0;
            for (const One of Session.Entries) { if (One.FromFile === true || One.Ids.length > 0) { Out = Out + 1; } }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.SavedCount");
            return 0;
        }
    };

    /** The browser's own day of an ISO time, as `YYYY-MM-DD`, or "" when it is not a time. */
    public static DayKey = (Iso: string, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let When: Date = Now;
            if (nz(Iso).length > 0) { When = new Date(nz(Iso)); }
            if (isNaN(When.getTime()) === true) { return ""; }
            return AppChatRules.DayKey(When, "-");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.DayKey");
            return "";
        }
    };

    /** The day line: "Today", "Yesterday", or "Mon 21 Sep" (with the year when it is not this year). */
    public static DayLabel = (Iso: string, Now: Date): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Key: string = AppAssistRules.DayKey(Iso, Now);
            if (Key === AppAssistRules.DayKey("", Now)) { return "Today"; }
            const Before: Date = new Date(Now.getTime() - 86400000);
            if (Key === AppAssistRules.DayKey("", Before)) { return "Yesterday"; }
            const When: Date = new Date(nz(Iso));
            if (isNaN(When.getTime()) === true) { return ""; }
            const Opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" };
            if (When.getFullYear() !== Now.getFullYear()) { Opts.year = "numeric"; }
            return When.toLocaleDateString("en-GB", Opts);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.DayLabel");
            return "";
        }
    };

    /** What became of a draft, in words. DOCUMENTED EXCEPTION: pure lookup; cannot throw. */
    public static OutcomeLabel = (Outcome: string): string => {
        if (Outcome === "sent") { return "Sent"; }
        if (Outcome === "edited") { return "Taken into the message box"; }
        if (Outcome === "skipped") { return "Skipped"; }
        return "";
    };

    // ── The panel's open/closed choice, remembered per browser ───────────────────────────────

    /** The values `AppAiPanelStore` keeps (every read and write wrapped - the accessor THROWS in private mode). */
    public static readonly PanelClosed: string = "closed";
    public static readonly PanelOpen: string = "open";

    /**
     * Open on the first contact? ⭐ Desktop (the panel is a column): OPEN unless this browser's owner closed
     * it (`Stored` = "closed"). Phone (a sheet over the chat): always shut - the ✦ button shows a count.
     * DOCUMENTED EXCEPTION: pure decision. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static OpenAtStart = (Wide: boolean, Stored: string): boolean => {
        return Wide === true && nz(Stored) !== AppAssistRules.PanelClosed;
    };

    // ── The answer's simple Markdown ─────────────────────────────────────────────────────────
    //
    // ⛔ THIS ONLY SPLITS TEXT INTO MARKED RUNS; `AppAssistPanel` draws every run with `textContent`.
    // No HTML is ever produced here, so `<img onerror>` in an answer stays those characters.
    // ⚠ Supported: blank-line paragraphs, `- `/`• `/`* ` bullets, `1.`/`1)` numbers, `**bold**`,
    // `*italic*`/`_italic_`, `` `code` ``, pipe tables (header, `---` separator row, body rows) and a
    // CLOSED ```wac-html fence (`rich`). Anything else - headings, links - stays as text.
    // ⛔ A `rich` block is only CARRIED here, as its raw source; the panel cleans it with the shared
    // allow-list before a single element exists. An unclosed fence and a table without its separator
    // row are ordinary text.

    /** The answer as paragraphs, lists, tables and rich blocks. */
    public static Blocks = (Text: string): AppAssistBlock[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistBlock[] = [];
            const All: string[] = nz(Text).replace(/\r\n?/g, "\n").split("\n");
            let Open: AppAssistBlock | null = null;
            let At: number = 0;
            while (At < All.length) {
                const Next: number = AppAssistRules.WholeBlockAt(Out, All, At);
                if (Next > At) { Open = null; At = Next; continue; }
                const Line: AppAssistLine = AppAssistRules.LineOf(All[At]);
                At = At + 1;
                if (Line.Kind === "blank") { Open = null; continue; }
                if (Open === null || (Open as AppAssistBlock).Kind !== Line.Kind) {
                    Open = new AppAssistBlock();
                    Open.Kind = Line.Kind;
                    Open.Start = Line.Start;
                    Out.push(Open);
                }
                (Open as AppAssistBlock).Lines.push(AppAssistRules.Spans(Line.Rest, false, false));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Blocks");
            return [];
        }
    };

    /**
     * A block that takes whole lines - a closed ```wac-html fence or a pipe table - starting at `At`:
     * pushed onto `Out`, answering the line after it. Answers `At` when neither starts there.
     */
    private static WholeBlockAt = (Out: AppAssistBlock[], All: string[], At: number): number => {
        if (getCoreUtils().KErrorMode === true) { return At; }
        try {
            const End: number = AppAssistRich.CloseAt(All, At);
            if (End > At) {
                const Rich: AppAssistBlock = new AppAssistBlock();
                Rich.Kind = "rich";
                Rich.Source = All.slice(At + 1, End).join("\n");
                Out.push(Rich);
                return End + 1;
            }
            return AppAssistRules.TableAt(Out, All, At);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.WholeBlockAt");
            return At;
        }
    };

    /**
     * A pipe table at `At`: a header row with a `|`, then a separator row whose cells are all
     * `---` (optionally `:` at either end for alignment) with as many cells, then every following
     * non-blank row with a `|`. Answers the line after it, or `At` when there is none.
     * ⚠ A body row with too few cells is padded, too many are cut - the header decides the width.
     */
    private static TableAt = (Out: AppAssistBlock[], All: string[], At: number): number => {
        if (getCoreUtils().KErrorMode === true) { return At; }
        try {
            if (At + 1 >= All.length || All[At].indexOf("|") < 0 || All[At + 1].indexOf("|") < 0) { return At; }
            const Head: string[] = AppAssistRules.CellsOf(All[At]);
            const Align: string[] = AppAssistRules.AlignOf(All[At + 1]);
            if (Head.length === 0 || Align.length !== Head.length) { return At; }
            const Table: AppAssistBlock = new AppAssistBlock();
            Table.Kind = "table";
            Table.Align = Align;
            for (const Cell of Head) { Table.Head.push(AppAssistRules.Spans(Cell, false, false)); }
            let Row: number = At + 2;
            while (Row < All.length && All[Row].trim().length > 0 && All[Row].indexOf("|") >= 0) {
                Table.Rows.push(AppAssistRules.RowOf(AppAssistRules.CellsOf(All[Row]), Head.length));
                Row = Row + 1;
            }
            Out.push(Table);
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.TableAt");
            return At;
        }
    };

    /** A body row's cells as runs, exactly `Width` of them. */
    private static RowOf = (Cells: string[], Width: number): AppAssistSpan[][] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistSpan[][] = [];
            for (let Col: number = 0; Col < Width; Col = Col + 1) {
                let Cell: string = "";
                if (Col < Cells.length) { Cell = Cells[Col]; }
                Out.push(AppAssistRules.Spans(Cell, false, false));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.RowOf");
            return [];
        }
    };

    /** A row's cells: outer pipes dropped, split on every `|` that is not `\|`, each trimmed. */
    public static CellsOf = (Row: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            let Text: string = nz(Row).trim();
            if (Text.charAt(0) === "|") { Text = Text.substring(1); }
            if (Text.length > 0 && Text.charAt(Text.length - 1) === "|" && Text.charAt(Text.length - 2) !== "\\") {
                Text = Text.substring(0, Text.length - 1);
            }
            const Out: string[] = [];
            let Cell: string = "";
            for (let At: number = 0; At < Text.length; At = At + 1) {
                const Char: string = Text.charAt(At);
                if (Char === "\\" && Text.charAt(At + 1) === "|") { Cell = Cell + "|"; At = At + 1; continue; }
                if (Char === "|") { Out.push(Cell.trim()); Cell = ""; continue; }
                Cell = Cell + Char;
            }
            Out.push(Cell.trim());
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.CellsOf");
            return [];
        }
    };

    /**
     * A separator row's alignment per column, or [] when any cell is not dash-shaped.
     * ⚠ GFM: ONE or more dashes (`-`, `--:`, `:-:`) - demanding three drew `|---|--:|` tables as text.
     */
    public static AlignOf = (Row: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            for (const Cell of AppAssistRules.CellsOf(Row)) {
                const Found: RegExpExecArray | null = /^(:?)-+(:?)$/.exec(Cell.replace(/\s+/g, ""));
                if (Found === null) { return []; }
                let Side: string = "";
                if (Found[1] === ":" && Found[2] === ":") { Side = "center"; }
                if (Found[1] === ":" && Found[2] !== ":") { Side = "left"; }
                if (Found[1] !== ":" && Found[2] === ":") { Side = "right"; }
                Out.push(Side);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.AlignOf");
            return [];
        }
    };

    /** One line: blank, a bullet, a numbered item, or paragraph text. */
    public static LineOf = (Raw: string): AppAssistLine => {
        if (getCoreUtils().KErrorMode === true) { return new AppAssistLine(); }
        try {
            const Out: AppAssistLine = new AppAssistLine();
            const Text: string = nz(Raw).trim();
            Out.Rest = Text;
            if (Text.length === 0) { Out.Kind = "blank"; return Out; }
            const Bullet: RegExpExecArray | null = /^[-•*]\s+(\S.*)$/.exec(Text);
            if (Bullet !== null) { Out.Kind = "ul"; Out.Rest = Bullet[1]; return Out; }
            const Number: RegExpExecArray | null = /^(\d{1,3})[.)]\s+(\S.*)$/.exec(Text);
            if (Number !== null) { Out.Kind = "ol"; Out.Start = parseInt(Number[1], 10); Out.Rest = Number[2]; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.LineOf");
            return new AppAssistLine();
        }
    };

    /** One line's runs. `Bold`/`Italic` are what the runs around it already carry. */
    public static Spans = (Text: string, Bold: boolean, Italic: boolean): AppAssistSpan[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppAssistSpan[] = [];
            const Line: string = nz(Text);
            let Plain: string = "";
            let At: number = 0;
            while (At < Line.length) {
                const Mark: string = AppAssistRules.MarkAt(Line, At);
                const End: number = AppAssistRules.CloseOf(Line, At, Mark);
                if (End < 0) { Plain = Plain + Line.charAt(At); At = At + 1; continue; }
                AppAssistRules.Push(Out, Plain, Bold, Italic, false);
                Plain = "";
                const Inner: string = Line.substring(At + Mark.length, End);
                if (Mark === "`") { AppAssistRules.Push(Out, Inner, Bold, Italic, true); }
                if (Mark === "**") { for (const One of AppAssistRules.Spans(Inner, true, Italic)) { Out.push(One); } }
                if (Mark === "*" || Mark === "_") { for (const One of AppAssistRules.Spans(Inner, Bold, true)) { Out.push(One); } }
                At = End + Mark.length;
            }
            AppAssistRules.Push(Out, Plain, Bold, Italic, false);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Spans");
            return [];
        }
    };

    /** The marker that could open a run at `At`: "`", "**", "*", "_", or "". */
    private static MarkAt = (Line: string, At: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Line.charAt(At) === "`") { return "`"; }
            if (Line.substring(At, At + 2) === "**") { return "**"; }
            if (Line.charAt(At) === "*" || Line.charAt(At) === "_") { return Line.charAt(At); }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.MarkAt");
            return "";
        }
    };

    /**
     * Where the run `Mark` opened at `At` closes, or -1 when it does not (and the marker is text).
     * ⚠ A run never starts or ends on a space ("2 * 3 * 4" is arithmetic), and `_` only counts at a
     * word's edge (`snake_case_name` is a name).
     */
    private static CloseOf = (Line: string, At: number, Mark: string): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            if (Mark.length === 0) { return -1; }
            const From: number = At + Mark.length;
            const End: number = Line.indexOf(Mark, From);
            if (End <= From) { return -1; }
            if (Mark === "`") { return End; }
            if (/\s/.test(Line.charAt(From)) === true || /\s/.test(Line.charAt(End - 1)) === true) { return -1; }
            if (Mark === "*" && Line.charAt(From) === "*") { return -1; }
            const Word: RegExp = /[A-Za-z0-9À-ɏ]/;
            if (Mark === "_" && (Word.test(Line.charAt(At - 1)) === true || Word.test(Line.charAt(End + 1)) === true)) { return -1; }
            return End;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.CloseOf");
            return -1;
        }
    };

    private static Push = (Out: AppAssistSpan[], Text: string, Bold: boolean, Italic: boolean, Code: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Text.length === 0) { return; }
            const Span: AppAssistSpan = new AppAssistSpan();
            Span.Text = Text;
            Span.Bold = Bold;
            Span.Italic = Italic;
            Span.Code = Code;
            Out.push(Span);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Push");
        }
    };

    /**
     * Mark the contact page as shown INSIDE the app, in its own markup, before it is ever painted:
     * `<html data-wac-embedded="1">`. The page then lays itself out as a chat (compact header, the
     * thread down to the frame's bottom edge, Details beside or in a drawer).
     *
     * ⚠ THE ATTRIBUTE IS ALSO SET ON LOAD (`AppChatAppend`), which is the contract; writing it into
     * the markup as well is what stops the page painting its standalone layout first and jumping.
     * ⚠ Only the FIRST `<html` tag is touched; a page without one is returned unchanged.
     */
    public static Embedded = (Html: string): string => {
        if (getCoreUtils().KErrorMode === true) { return Html; }
        try {
            return AppFrameWide.MarkAttr(nz(Html), AppAssistRules.EmbeddedAttr, "AppAssistRules.Embedded");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRules.Embedded");
            return Html;
        }
    };
}
